import React, { useState, useMemo } from 'react';
import connect from '../utils/connect';
import { getRHOptions } from '../components/filters/getSpecificOptions';
import getOptions from '../utils/getFilterOptions';
import getPlacementOptions from '../utils/getPlacementReducedOptions';
import { getPlatformTypeOptions } from './options';
import {
    statusOptions,
    processing,
    onTrack,
    offTrack,
    goalReached,
    pending,
} from '../constants/pacingStatus';
import { metricOptions } from '../constants/pacingMetrics';

export const PacingContext = React.createContext({});

type PacingProviderProps = {
    children: React.ReactNode;
    filters: any;
    pacing: any;
};

const PacingProvider = ({ children, filters, pacing }: PacingProviderProps) => {
    const {
        rightsholders,
        shouldShowLeagueHierarchy,
        sponsors,
        placements,
        platformPostTypePairs: { list: platformList },
    } = filters;

    const { goals } = pacing;

    const [rhs, setRhs] = useState({});
    const [sps, setSps] = useState({});
    const [plc, setPlc] = useState({});
    const [pts, setPts] = useState({});
    const [currentGoalId, setCurrentGoalId] = useState(null);
    const [metrics, setMetrics] = useState({});
    const [status, setStatus] = useState({
        [`current-${processing}`]: true,
        [`current-${onTrack}`]: true,
        [`current-${offTrack}`]: true,
        [`current-${goalReached}`]: true,
    });

    const rightsholdersOptions = useMemo(
        () =>
            getRHOptions({
                rhList: rightsholders.list,
                shouldShowLeagueHierarchy: false,
            }),
        [rightsholders, shouldShowLeagueHierarchy],
    );
    const sponsorsOptions = useMemo(
        () =>
            getOptions({
                list: sponsors.list,
            }),
        [sponsors],
    );

    const placementOptions = useMemo(
        () =>
            getPlacementOptions({
                list: placements.list,
            }),
        [placements],
    );

    const platformTypeOptions = useMemo(
        () => getPlatformTypeOptions(Object.keys(platformList)),
        [platformList],
    );

    const metricsOpts = useMemo(
        () =>
            metricOptions.map(({ value, name: label }) => {
                return {
                    value,
                    label,
                };
            }),
        [],
    );

    const currentoGoalOptions = useMemo(() => {
        return goals
            .filter(({ statusId }) =>
                [pending, processing].every(status => status !== statusId),
            )
            .map(({ id, name }) => {
                return {
                    value: id,
                    name,
                };
            });
    }, [goals]);

    const providerValue = {
        filter: {
            rightsholderIds: {
                selected: rhs,
                onChange: setRhs,
                options: rightsholdersOptions,
                isNested: false,
            },
            sponsorIds: {
                selected: sps,
                onChange: setSps,
                options: sponsorsOptions,
                isNested: false,
            },
            rightsholderPlacementIds: {
                selected: plc,
                onChange: setPlc,
                options: placementOptions,
                isNested: false,
            },
            platformTypeIds: {
                selected: pts,
                onChange: setPts,
                options: platformTypeOptions,
                isNested: false,
            },
            metricIds: {
                selected: metrics,
                onChange: setMetrics,
                options: metricsOpts,
                isNested: false,
            },
            statusIds: {
                selected: status,
                onChange: setStatus,
                options: statusOptions,
                isNested: true,
            },
        },
        currentGoalId,
        setCurrentGoalId,
        currentoGoalOptions,
    };

    return (
        <PacingContext.Provider value={providerValue}>
            {children}
        </PacingContext.Provider>
    );
};

export default connect(['filters', 'pacing', 'client'])(PacingProvider);
